import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import clsx from 'clsx';

import client from 'api/apollo-client';
import { getServerPageGetWobRequests } from 'api/graphql-helper';
import { WobRequestOverviewFieldsFragment } from 'api/graphql-types';
import {
	setWobRequestItems,
	setTotalWobRequestItems,
	setIsLoading,
	setCurrentPage,
	setSort,
	resetStore
} from 'redux/stores/wob-requests';
import { useAppSelector } from 'redux/store';
import { Pagination } from 'components/pagination';
import { EmptyState } from 'components/empty-state/empty-state';
import { Sort } from 'components/sort';
import { Button } from 'components/button';
import { CardDownload } from 'components/card';
import { Icon } from 'components/icon/icon';
import useDidMountEffect from 'utils/hooks/use-mount-effect';

import IconSelect from 'public/assets/icons/union.svg';

import styles from './wob-request-overview.module.scss';
import { basicSlugify } from 'utils/slugify';

export interface IWobRequestOverviewProps {
	module?: WobRequestOverviewFieldsFragment;
}

export const WobRequestOverview = ({ module }: IWobRequestOverviewProps): React.JSX.Element => {
	const { publicationState } = useAppSelector((state) => state.settings);
	const intl = useIntl();
	const dispatch = useDispatch();
	const router = useRouter();
	const overviewRef = useRef<HTMLDivElement>(null);

	const { locale } = router;
	const { currentPage, wobRequestItems, totalWobRequestItems, isLoading, sort } = useAppSelector(
		(state) => state.wobRequests
	);

	const limit = 10;
	const start = (currentPage - 1) * limit;
	const totalNumberOfPages = Math.ceil(totalWobRequestItems / limit);
	const hasResults = wobRequestItems && wobRequestItems.length > 0;
	const sortOptions = [
		{
			id: 'date:DESC',
			label: intl.formatMessage({ id: 'modules.wobRequestOverview.sortNewToOld' }),
			shortLabel: intl.formatMessage({ id: 'modules.wobRequestOverview.newToOld' })
		},
		{
			id: 'date:ASC',
			label: intl.formatMessage({ id: 'modules.wobRequestOverview.sortOldToNew' }),
			shortLabel: intl.formatMessage({ id: 'modules.wobRequestOverview.oldToNew' })
		}
	];

	const [sortIsOpen, setSortIsOpen] = useState<boolean>(false);

	const getWobRequestData = async (): Promise<void> => {
		dispatch(setIsLoading(true));
		try {
			const result = await getServerPageGetWobRequests(
				{ variables: { limit, start, locale, sort: String(sort.id), publicationState } },
				client
			);
			const { wobRequests_connection: wobRequests } = result.props.data;

			dispatch(setWobRequestItems(wobRequests?.data?.map((item) => item.attributes)));
			dispatch(setTotalWobRequestItems(wobRequests?.pageInfo.total || 0));
			dispatch(setIsLoading(false));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error', error);
		}
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 120;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.pageYOffset - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			scrollToTop();
			dispatch(setCurrentPage(page));
		}
	};

	const handleSortOnChange = (selectedSortFilter: { id: string; label: string } | undefined): void => {
		if (selectedSortFilter) dispatch(setSort(selectedSortFilter));
	};

	useDidMountEffect(() => {
		getWobRequestData();
	}, [sort, currentPage, currentPage, locale]);

	useEffect(() => {
		if (sortIsOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'visible';
		}
	}, [sortIsOpen]);

	useEffect(() => {
		return () => {
			dispatch(resetStore());
		};
	}, []);

	return (
		<div className="container" ref={overviewRef}>
			<div className={styles.header}>
				<h2>{module?.sectionTitle || intl.formatMessage({ id: 'modules.wobRequestOverview.title' })}</h2>

				<Button
					variant="secondary-light"
					title={intl.formatMessage({ id: 'global.sort' })}
					icon={<Icon customIcon={<IconSelect />} size="14px" />}
					onClick={() => setSortIsOpen(!sortIsOpen)}
				/>

				<Sort
					onChange={handleSortOnChange}
					mobileIsOpen={sortIsOpen}
					setMobileIsOpen={setSortIsOpen}
					customOptions={sortOptions}
					stateType="wobRequests"
				/>
			</div>

			<div className={clsx(styles.results, hasResults && isLoading && styles.resultsLoading)}>
				{hasResults ? (
					wobRequestItems?.map(({ title, zip, date }) => (
						<CardDownload key={`wob-request-${basicSlugify(title)}`} title={title} date={date} zip={zip} />
					))
				) : (
					<EmptyState />
				)}
			</div>

			<Pagination currentPage={currentPage} onPageChange={handlePageChange} totalPages={totalNumberOfPages} />
		</div>
	);
};

export default WobRequestOverview;
